import PropTypes from "prop-types";
import UserProdutividadeDia from './UserProdutividadeDia';
import UserProdutividadeHora from './UserProdutividadeHora';
import UserDashboardImagens from './UserDashboardImagens';
import {
  Typography,
  Avatar,
  Card,
  CardContent,
  Grid,
  Box,
} from "@mui/material";
import GaugeChart from "react-gauge-chart";
import { formatDate, usePermiteAcesso } from "../../Components/Funcionais/Utils";
import { useEffect, useState } from "react";
import { PostAPI } from "../../Components/Backend/PostAPI";
import APIAxios from "../../Api/APIAxios";
import SessaoAuth from "../../Hooks/SessaoAuth";
import UserTabelaProgram from "./UserTabelaProgram";
import PageTitle from "../../components/Layout/PageTitle";

const URL_ProdHora = "/produtividade/hora";
const URL_ProdDia = "/produtividade/dia";

const gaugeCardSx = (topGradient, bgGradient, borderColor, shadowColor) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  background: bgGradient,
  boxShadow: `0 8px 28px ${shadowColor}, 0 2px 6px rgba(0,0,0,0.06)`,
  borderRadius: "16px",
  border: `1px solid ${borderColor}`,
  overflow: "hidden",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0, left: 0, right: 0,
    height: "4px",
    background: topGradient,
  },
});

const UserDashboard = ({ user = {}, overallProductivity = "0%", setSelectedUser, usuarios }) => {

  usePermiteAcesso(7);
  const [avisoErro, setAvisoErro] = useState("");
  const [avisoSucesso, setAvisoSucesso] = useState("");
  const [loading, setLoading] = useState(false);

  const apiAxios = APIAxios();
  const { sessao } = SessaoAuth();
  const [dataProd, setDataProd] = useState([]);
  const [dataProdDia, setDataProdDia] = useState([]);
  const [hora, setHora] = useState();
  const [diaProdHora, setDiaProdHora] = useState();
  const [abrirVideo, setAbrirVideo] = useState(false);
  const [exibeVideos, setExibeVideos] = useState(false);

  const handleExibirVideos = () => {
    setExibeVideos(!exibeVideos);
  };

  const loadProdHora = async () => {

    setLoading(true);

    const response = await PostAPI(apiAxios, URL_ProdHora,
      {
        data_inicio: diaProdHora,
        data_fim: diaProdHora,
        id_usuario: user.idColaborador,
      });
    setLoading(false);

    if (!response.success) {
      setAvisoErro(response.errorMessage || "Erro ao carregar dados de produtividade por hora.");
    } else {
      setDataProd(response.data);
      setHora(response.data[0].hora);
    }
  };

  useEffect(() => {
    if (diaProdHora) {
      loadProdHora();
    }
  }, [diaProdHora]);

  const loadProdDia = async () => {

    setLoading(true);
    const response = await PostAPI(apiAxios, URL_ProdDia,
      {
        data_inicio: sessao.dataIni,
        data_fim: sessao.dataFim,
        id_usuario: user.idColaborador,
      });

    setLoading(false);

    if (!response.success) {
      setAvisoErro(response.errorMessage || "Erro ao carregar dados de produtividade.");
    } else {
      if (response.data.length > 0) {
        setDiaProdHora(formatDate(response.data[0].data));
        setDataProdDia(response.data);
      } else {
        setDiaProdHora(null);
        setDataProdDia([]);
      }
    }
  };

  useEffect(() => {
    loadProdDia();
  }, [sessao]);

  const calcularProdutividadeGrupo = (users, selecionado) => {

    if (users.length === 0) return 0;

    const usersDoGrupo = users.filter(u => u.idGrupo === selecionado.idGrupo);

    if (usersDoGrupo.length === 0) return 0;

    const total = usersDoGrupo.reduce(
      (sum, u) => sum + u.minTotal,
      0
    );

    const registrado = usersDoGrupo.reduce(
      (sum, u) => sum + u.minRegistrados,
      0
    );

    return (registrado * 100 / total) > 100 ? 100 : (registrado * 100 / total).toFixed(2);
  };

  const produtividadeGrupo = calcularProdutividadeGrupo(usuarios, user);

  return (
    <Box>
      <PageTitle
        caption="Análise por Colaborador"
        setAvisoErro={setAvisoErro}
        avisoErro={avisoErro}
        avisoSucesso={avisoSucesso}
        setAvisoSucesso={setAvisoSucesso}
        loading={loading}
        accentStyle
      />

      <Box sx={{ px: 2, pb: 2 }}>
        <Grid container spacing={2}>

          {/* Perfil do colaborador */}
          <Grid item xs={12} md={6} xl={3}>
            <Card sx={gaugeCardSx(
              "linear-gradient(90deg, #455A64, #78909C, #B0BEC5)",
              "linear-gradient(145deg, #f5f7fa 0%, #eaecef 100%)",
              "rgba(96, 125, 139, 0.2)",
              "rgba(69, 90, 100, 0.15)"
            )}>
              <Box sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
                gap: 1,
              }}>
                <Box sx={{
                  p: "3px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #455A64, #78909C, #B0BEC5)",
                  boxShadow: "0 4px 16px rgba(69, 90, 100, 0.3)",
                }}>
                  <Avatar
                    src={user.avatar || ""}
                    alt={user.nomeColaborador || "Usuário"}
                    sx={{
                      width: 88,
                      height: 88,
                      border: "3px solid white",
                    }}
                  />
                </Box>
                <Typography align="center" variant="h6" sx={{
                  fontWeight: 700,
                  color: "#37474F",
                  mt: 1,
                }}>
                  {user.nomeColaborador || "Não Identificado"}
                </Typography>
                {user.nomeGrupo && (
                  <Box sx={{
                    px: 1.5,
                    py: 0.4,
                    borderRadius: "12px",
                    background: "rgba(69, 90, 100, 0.1)",
                    border: "1px solid rgba(69, 90, 100, 0.2)",
                  }}>
                    <Typography variant="caption" sx={{
                      color: "#546E7A",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                    }}>
                      {user.nomeGrupo}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>

          {/* Produtividade Individual */}
          <Grid item xs={12} md={6} xl={3}>
            <Card sx={gaugeCardSx(
              "linear-gradient(90deg, #1A3865, #647DEE, #269FFB)",
              "transparent",
              "rgba(100, 125, 238, 0.2)",
              "rgba(26, 56, 101, 0.15)"
            )}>
              <CardContent sx={{ flexGrow: 1, pt: 3, pb: "16px !important" }} align="center">
                <Typography variant="subtitle2" sx={{
                  fontWeight: 700,
                  color: "#1A3865",
                  letterSpacing: "0.4px",
                  mb: 0.5,
                }}>
                  Aproveitamento Individual
                </Typography>
                <GaugeChart
                  align="center"
                  id="user-gauge-individual"
                  nrOfLevels={3}
                  percent={user.minRegistrados * 100 / user.minTotal > 100 ? 1 : (user.minRegistrados / user.minTotal).toFixed(4)}
                  arcsLength={[0.6, 0.2, 0.2]}
                  colors={["#E57373", "#FFD54F", "#81C784"]}
                  arcWidth={0.25}
                  arcPadding={0.02}
                  textColor={"#1A3865"}
                  needleColor={"#78909C"}
                  animate={false}
                  style={{ width: "100%", maxWidth: "220px" }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Produtividade do Grupo */}
          <Grid item xs={12} md={6} xl={3}>
            <Card sx={gaugeCardSx(
              "linear-gradient(90deg, #4527A0, #7E57C2, #B39DDB)",
              "transparent",
              "rgba(126, 87, 194, 0.2)",
              "rgba(69, 39, 160, 0.15)"
            )}>
              <CardContent sx={{ flexGrow: 1, pt: 3, pb: "16px !important" }} align="center">
                <Typography variant="subtitle2" sx={{
                  fontWeight: 700,
                  color: "#4527A0",
                  letterSpacing: "0.4px",
                  mb: 0.5,
                }}>
                  Aproveitamento Médio do Grupo
                </Typography>
                <GaugeChart
                  align="center"
                  id="user-gauge-grupo"
                  nrOfLevels={3}
                  percent={produtividadeGrupo / 100}
                  arcsLength={[0.6, 0.2, 0.2]}
                  colors={["#E57373", "#FFD54F", "#81C784"]}
                  arcWidth={0.25}
                  arcPadding={0.02}
                  textColor={"#1A3865"}
                  needleColor={"#78909C"}
                  animate={false}
                  style={{ width: "100%", maxWidth: "220px" }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Produtividade da Empresa */}
          <Grid item xs={12} md={6} xl={3}>
            <Card sx={gaugeCardSx(
              "linear-gradient(90deg, #2e7d32, #4caf50, #81c784)",
              "transparent",
              "rgba(76, 175, 80, 0.2)",
              "rgba(46, 125, 50, 0.15)"
            )}>
              <CardContent sx={{ flexGrow: 1, pt: 3, pb: "16px !important" }} align="center">
                <Typography variant="subtitle2" sx={{
                  fontWeight: 700,
                  color: "#2e7d32",
                  letterSpacing: "0.4px",
                  mb: 0.5,
                }}>
                  Aproveitamento Médio da Empresa
                </Typography>
                <GaugeChart
                  align="center"
                  id="user-gauge-empresa"
                  nrOfLevels={3}
                  percent={overallProductivity / 100}
                  arcsLength={[0.6, 0.2, 0.2]}
                  colors={["#E57373", "#FFD54F", "#81C784"]}
                  arcWidth={0.25}
                  arcPadding={0.02}
                  textColor={"#1A3865"}
                  needleColor={"#78909C"}
                  animate={false}
                  style={{ width: "100%", maxWidth: "220px" }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ px: 2, pb: 2 }}>
        <Grid container spacing={2}>
          <Grid item md={12} lg={8} style={{width:"100%"}}>
            <UserProdutividadeDia
              setDiaProdHora={setDiaProdHora}
              dataProdDia={dataProdDia}
            />
          </Grid>
          <Grid item md={12} lg={4} style={{width:"100%"}}>
            <UserProdutividadeHora
              dataProd={dataProd}
              setHora={setHora}
              diaProdHora={diaProdHora}
              handleExibirVideos={handleExibirVideos}
              exibeVideos={exibeVideos}
              setAbrirVideo={setAbrirVideo}
              abrirVideo={abrirVideo}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ px: 2, pb: 2 }}>
        <UserTabelaProgram
          diaProdHora={diaProdHora}
          user={user}
        />
      </Box>

      <Box sx={{ px: 2, pb: 2 }}>
        <UserDashboardImagens
          dia={diaProdHora}
          hora={hora}
          user={user}
          exibeVideos={exibeVideos}
          abrirVideo={abrirVideo}
        />
      </Box>
    </Box>
  );
};

UserDashboard.propTypes = {
  user: PropTypes.object,
  overallProductivity: PropTypes.string,
  setSelectedUser: PropTypes.func,
  usuarios: PropTypes.array,
};

export default UserDashboard;
